import UrlService from 'common/services/Url';

/**
 * Adaptateurs richfield → props des composants de présentation.
 *
 * Chaque fonction est pure (`section → props`), testable isolément, et rend
 * exactement les props que le composant statique reçoit déjà depuis
 * `staticContent.js` — c'est ce qui permet la commutation bloc par bloc décrite
 * au §12 du plan, sans toucher aux composants de présentation.
 *
 * §6.2 du plan : la section reçue est déjà filtrée par référence/fenêtre de
 * publication et déjà interpolée côté serveur. On ne fait ici que la mise en
 * forme d'affichage (tri déjà fait par useRichFields, forçage https…).
 */

/**
 * `UrlService.modifyHTTP` retire le protocole puis re-préfixe `https://` sans
 * condition : il est fait pour les URLs S3 stockées sans protocole. Sur un
 * chemin relatif (`/react-templates/…/photo.jpg`, ce que porte le JSON de seed
 * pour rester indépendant de l'environnement) il produirait
 * `https:///react-templates/…` — trois slashes, image cassée. On ne force donc
 * https que sur ce qui a un hôte.
 */
const httpsOrEmpty = (url) => {
  if (!url) return '';
  return url.startsWith('/') ? url : UrlService.modifyHTTP(url);
};

/**
 * Titre bicolore (`{ line1, highlight }`) depuis un champ BO à ligne unique.
 *
 * `SectionHead` rend `line1`, un `<br>`, puis `highlight` en rose canon — c'est
 * le `Canon.<br><em>À prix canon.</em>` de la maquette. Or le BO n'offre qu'un
 * `<input type="text">` pour `title` : ni seconde ligne, ni second champ. La
 * convention est donc un séparateur `|` dans le texte saisi
 * (« Canon.|À prix canon. »).
 *
 * Sans séparateur, tout part dans `line1` et le titre s'affiche en une seule
 * couleur : dégradé, jamais cassé — c'est le comportement de repli si le client
 * ignore la convention. À confirmer avec Idir au paramétrage (c'est une
 * consigne de saisie côté BO, pas du code).
 */
const splitTitle = (raw) => {
  const [line1 = '', highlight = ''] = (raw || '').split('|');
  return { line1: line1.trim(), highlight: highlight.trim() };
};

/**
 * PLAN_CANON / TARIFS → props de PriceGrid (view_kind NEWSFEED).
 *
 * `isFeatured`/`ribbon` (« TOP DES VENTES ») n'existent pas comme champ
 * richfield dédié — la règle métier (Romain/Idir, 02/09 : jamais plus de 3
 * forfaits, toujours celui du milieu mis en avant) est donc dérivée par
 * position plutôt qu'attendue depuis la donnée, pour rester cohérente avec
 * `PRICES.cards` de `staticContent.js` (2ᵉ carte sur 3) sans dépendre d'un
 * champ que le BO n'expose pas.
 *
 * `badge` (le prix, ex. « Courts 27 € · Longs 32 € ») réutilise `cta_label` :
 * le contrat richfield générique n'a que `title`/`desc`/`cta_label` comme
 * champs texte, et `desc` porte déjà la liste des prestations — il n'y a pas
 * de second champ texte disponible pour le prix.
 *
 * `href` (spec 2.d/3.e, « et lien ») lit `cta_target`, jusqu'ici ignoré alors
 * que le BO le propose déjà à la saisie : `PriceCard` rend toute la carte
 * cliquable plutôt que d'ajouter un lien texte, faute de champ libre pour son
 * libellé (voir `ui/PriceGrid.js`).
 */
export const toPriceGridProps = (section) => {
  if (!section) return null;
  const fields = section.fields || [];
  const featuredIndex = fields.length ? Math.floor((fields.length - 1) / 2) : -1;
  return {
    // Pas d'`eyebrow` dérivé de `section.title` : ce serait le texte du `<h2>`
    // répété juste au-dessus de lui. La section richfield n'a pas de champ
    // distinct pour le surtitre — c'est donc à l'appelant de le passer quand il
    // en veut un (le listing en affiche un, l'onglet Tarifs de la fiche non).
    title: splitTitle(section.title),
    subtitle: section.desc || '',
    cta: section.cta_label ? { label: section.cta_label, href: section.cta_target || '#' } : undefined,
    cards: fields.map((field, index) => ({
      image: httpsOrEmpty(field.image),
      title: field.title || '',
      desc: field.desc || '',
      badge: field.cta_label || '',
      // Carte entière cliquable quand le champ porte un lien (voir PriceCard) :
      // le contrat NEWSFEED n'a pas de champ texte libre pour un libellé dédié.
      href: field.cta_target || undefined,
      ...(index === featuredIndex ? { isFeatured: true, ribbon: 'TOP DES VENTES' } : null),
    })),
  };
};

/** EXPERTISES → props de ExpertiseGrid (view_kind NEWSFEED). */
export const toExpertiseGridProps = (section) => {
  if (!section) return null;
  return {
    title: splitTitle(section.title),
    subtitle: section.desc || '',
    link: section.cta_label ? { label: section.cta_label, href: section.cta_target || '#' } : undefined,
    cards: (section.fields || []).map((field) => ({
      image: httpsOrEmpty(field.image),
      imageAlt: field.title || '',
      title: field.title || '',
      text: field.desc || '',
      link: field.cta_label ? { label: field.cta_label, href: field.cta_target || '#' } : undefined,
    })),
  };
};

/** PLAN_CANON (variante bandeau) → props de PromoStrip (view_kind NEWSFEED, 1 champ). */
export const toPromoStripProps = (section) => {
  if (!section) return null;
  const [field] = section.fields || [];
  if (!field) return null;
  return {
    image: httpsOrEmpty(field.image),
    imageAlt: field.title || '',
    eyebrow: section.title || '',
    title: field.title || '',
    text: field.desc || '',
    cta: field.cta_label ? { label: field.cta_label, href: field.cta_target || '#' } : undefined,
  };
};

/** COLLECTION → props de CollectionSlider (view_kind NEWSFEED). */
export const toCollectionSliderProps = (section) => {
  if (!section) return null;
  return {
    eyebrow: section.title || '',
    slides: (section.fields || []).map((field) => {
      const title = splitTitle(field.title);
      return {
        src: httpsOrEmpty(field.image),
        // `alt` reprend le titre complet, séparateur retiré : le `|` est une
        // convention de mise en forme, il n'a rien à faire dans un texte lu par
        // un lecteur d'écran ou indexé.
        alt: [title.line1, title.highlight].filter(Boolean).join(' '),
        title,
        text: field.desc || '',
      };
    }),
  };
};

/**
 * SALON → props de SalonGallery (view_kind IMAGE_BANNER).
 *
 * Une photo par **champ**, pas un champ unique : `IMAGE_BANNER` n'expose
 * qu'`image` et `cta_target` par champ, donc N photos = N champs.
 *
 * **Aucune troncature.** La spec 3.b parle d'un diaporama de 5 photos maximum,
 * mais c'est une consigne éditoriale, pas une limite technique : `SalonGallery`
 * dérive ses points et son compteur de `photos.length` et accepte n'importe quel
 * nombre. Couper la 6ᵉ photo ici la ferait disparaître sans le moindre retour
 * côté BO — le client ajoute une photo, rien ne bouge, il conclut que c'est
 * cassé (constaté le 02/09). Si un plafond doit être tenu, sa place est dans le
 * BO, là où l'on peut le dire à celui qui saisit.
 *
 * `alt` reste vide quand le champ n'a pas de titre : `IMAGE_BANNER` ne propose
 * pas ce champ à la saisie côté BO (seul un import JSON peut le porter).
 * `SalonGallery` retombe alors sur « {nom du salon} — photo N sur M », ce qui
 * évite une image sans alternative textuelle.
 */
export const toSalonGalleryProps = (section) => {
  if (!section) return null;
  const photos = (section.fields || [])
    .filter((field) => field.image)
    .map((field) => ({ src: httpsOrEmpty(field.image), alt: field.title || '' }));
  return { photos };
};

/**
 * Sélection de la section SALON à afficher parmi celles remontées par
 * `useRichFields(RICHFIELD_KEYS.SALON)`.
 *
 * Cas produit ponctuel (Romain, 03/09) : TCHIP Miami doit pouvoir afficher une
 * galerie différente de tous les autres salons, alors que le contrat richfield
 * ne prévoit qu'une section par groupe pour toute la marque. Plutôt qu'étendre
 * le modèle, la marque saisit une **seconde** section `group: "SALON"` dédiée
 * (id `SECTION_TCHIP_SALON_MIAMI`, cf. `seed.brand1.json`) en plus de la
 * générique — et le discriminant est `extended_string_data_24`
 * (`extendedFields.js`, `salonGalleryVariant`) : une fois nettoyée (accents,
 * casse, espaces), sa valeur doit égaler exactement « tchip miami » pour que
 * la copie s'affiche. Toute autre valeur, y compris vide, retombe sur la
 * section générique — donc sur le comportement actuel pour tous les autres
 * salons.
 *
 * Volontairement peu générique : un seul salon est concerné aujourd'hui, un
 * mécanisme de correspondance id ⇄ variante à plusieurs entrées serait de la
 * généralisation anticipée pour un besoin qui n'existe pas encore.
 */
const MIAMI_SALON_SECTION_ID = 'SECTION_TCHIP_SALON_MIAMI';
const MIAMI_VARIANT_KEY = 'tchip miami';

const normalizeVariantKey = (value) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

export const selectSalonSection = (sections, variantKeyRaw) => {
  const list = sections || [];
  if (normalizeVariantKey(variantKeyRaw) === MIAMI_VARIANT_KEY) {
    const miami = list.find((section) => section.id === MIAMI_SALON_SECTION_ID);
    if (miami) return miami;
  }
  return list.find((section) => section.id !== MIAMI_SALON_SECTION_ID) || list[0];
};

export default {
  toPriceGridProps,
  toExpertiseGridProps,
  toPromoStripProps,
  toCollectionSliderProps,
  toSalonGalleryProps,
  selectSalonSection,
};
